import clsx from "clsx";
import type { FleetMetricsRuleCell, RuleId, RuleRow } from "../types";

/**
 * The numbered health-rule square — ONE encoding shared by the fleet row and
 * the hotel page's rules card, so a rule can never read red in the list and
 * green on the hotel (they used to derive the same tone twice, side by side).
 *
 * The tone ladder is the standing-warning rule (backend issue 45): a rule that
 * fired reads `fail` while its warning stands, whatever today's `result` says —
 * Rule 2 is edge-triggered and `fix_applied` blinds the windowed rules, so a
 * passing read is routine while the problem stands. Only an admin clears it.
 */
export type RuleSquareTone =
  | "pass"
  | "fail"
  | "verifying"
  | "relieved"
  | "insufficient"
  | "none";

const TONE_CLASS: Record<RuleSquareTone, string> = {
  pass: "bg-success-light text-success-dark",
  fail: "bg-danger-light text-danger-dark",
  verifying: "bg-info-light text-info-dark",
  relieved: "bg-[#F4F4F6] text-gray-600",
  insufficient: "border border-dashed border-gray-300 text-gray-400",
  none: "border border-dashed border-gray-300 text-gray-300",
};

const SIZE_CLASS = {
  sm: "w-5 h-5 rounded text-[11px]",
  md: "w-7 h-7 rounded-md text-[13px]",
} as const;

export function RuleSquare({
  rule,
  tone,
  size = "sm",
  title,
}: {
  rule: RuleId;
  tone: RuleSquareTone;
  size?: keyof typeof SIZE_CLASS;
  /** Hover hint — drawn by `TooltipLayer`, not by the browser. */
  title?: string;
}) {
  return (
    <span
      data-tip={title}
      className={clsx(
        "inline-flex items-center justify-center font-semibold shrink-0",
        SIZE_CLASS[size],
        TONE_CLASS[tone],
      )}
    >
      {rule}
    </span>
  );
}

/** The fleet row's cell (metrics read) → tone. */
export function toneFromFleetCell(
  cell: FleetMetricsRuleCell | undefined,
): RuleSquareTone {
  if (!cell) return "none";
  if (cell.result === "fail" && cell.mapsToAcceptedCondition) return "relieved";
  if (cell.standing) return cell.standing.unresolved ? "fail" : "verifying";
  if (cell.result === "pass") return "pass";
  if (cell.result === "fail") return "fail";
  return "insufficient";
}

/** The hotel page's evaluation row → tone. A blind window is never green. */
export function toneFromRuleRow(rule: RuleRow): RuleSquareTone {
  if (rule.mapsToAcceptedCondition) return "relieved";
  if (rule.standingWarning)
    return rule.standingWarning.unresolved ? "fail" : "verifying";
  if (rule.blindWindow) return "insufficient";
  if (rule.result === "pass") return "pass";
  if (rule.result === "fail") return "fail";
  return "insufficient";
}
